import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';

interface ServiceNodeData {
  label: string;
  description?: string;
  serviceType?: 'api' | 'frontend' | 'external' | 'microservice';
}

const icons: Record<string, string> = {
  api: '🔌',
  frontend: '🖥️',
  external: '🌐',
  microservice: '⚙️',
};

const colors: Record<string, { bg: string; border: string; handle: string }> = {
  api: { bg: 'bg-purple-50', border: 'border-purple-300', handle: '!bg-purple-500' },
  frontend: { bg: 'bg-cyan-50', border: 'border-cyan-300', handle: '!bg-cyan-500' },
  external: { bg: 'bg-slate-50', border: 'border-slate-300', handle: '!bg-slate-500' },
  microservice: { bg: 'bg-indigo-50', border: 'border-indigo-300', handle: '!bg-indigo-500' },
};

export const ServiceNode = memo(function ServiceNode({
  data,
  selected,
}: NodeProps) {
  const nodeData = data as unknown as ServiceNodeData;
  const serviceType = nodeData.serviceType || 'api';
  const icon = icons[serviceType] || '⚙️';
  const colorScheme = colors[serviceType] || colors.api;

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 min-w-[150px] ${colorScheme.bg} ${
        selected ? 'border-primary shadow-lg' : colorScheme.border
      }`}
    >
      <Handle type="target" position={Position.Top} className={colorScheme.handle} />

      <div className="flex items-center gap-2">
        <span className="text-xl">{icon}</span>
        <div>
          <div className="font-semibold text-sm">{nodeData.label}</div>
          {nodeData.description && (
            <div className="text-xs text-muted-foreground mt-1">
              {nodeData.description}
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className={colorScheme.handle} />
    </div>
  );
});
